import { useContext } from 'react';

import { Button, Heading, Modal, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import RentPricingChart from '../../../components/Freemium/components/RentPricingChart';
import { FreemiumContext } from '../../../components/Freemium/context';
import {
  useGetListingInquiries,
  useGetListingPrices,
  useGetPremiumComps,
  useGetPremiumOverview,
  useGetPremiumPricingGuidance,
} from '../../../hooks/queries/freemium';
import { RoutingSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';
import { StyledCard, StyledFlex } from './StyledComponents';

const pmrResultsBaseUrl = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

const PremiumPricingGuidanceModal = () => {
  const {
    freemiumState: {
      filters: { beds, filterType, propertyType },
    },
  } = useContext(FreemiumContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { data: overviewData } = useGetPremiumOverview({ listingId });

  const {
    data: inquiriesData,
    error: InquiriesDataError,
    refetch: refetchGetListingPerformance,
  } = useGetListingInquiries(
    {
      listingId,
      filterType: filterType,
      beds: beds,
      propertyType: propertyType,
    },
    {
      keepPreviousData: true,
    },
  );

  const {
    data: pricingGuidance,
    error: pricingGuidanceError,
    refetch: refetchGetPricingGuidance,
  } = useGetPremiumPricingGuidance({ listingId });

  const {
    data: compsData,
    error: compsDataError,
    refetch: refetchGetComps,
  } = useGetPremiumComps(
    {
      listingId,
      beds: beds,
      filterType: filterType,
      propertyType: propertyType,
    },
    {
      keepPreviousData: true,
    },
  );

  const { data: pricingData } = useGetListingPrices(
    { listingId, beds, filterType, propertyType },
    {
      keepPreviousData: true,
    },
  );

  if (!overviewData || !pricingData || !compsData || !inquiriesData) {
    return null;
  }

  const handleCompsButtonClick = () => window.open(overviewData.compsUrl, '_blank', 'noopener, noreferrer');

  const { formattedKebobCase: pmrResultsAddress } = propertyUtils.formatAddress(overviewData);
  const pmrUrl = new URL(`${pmrResultsBaseUrl}${pmrResultsAddress}/`);
  overviewData?.unit && pmrUrl?.searchParams && pmrUrl.searchParams.set('unit', overviewData.unit);

  const { cityState, days, hasEnoughListings, hasListings } = compsData || {};
  const percentChangeValue = cityState?.percentChange;

  const getMedianPriceHeading = () => {
    if (pricingGuidanceError || !pricingGuidance) {
      return (
        <Text>
          We are having issues loading your pricing guidance data.{' '}
          <TextButton onClick={() => refetchGetPricingGuidance()}>Retry now.</TextButton>
        </Text>
      );
    }
    const {
      beds,
      marketMedian: { price: medianPrice },
      propertyType: { display: propertyTypeDisplay },
      zip,
      city,
    } = pricingGuidance;

    if ((beds || beds === 0) && propertyTypeDisplay && zip && medianPrice) {
      if (beds === 0) {
        return `The median rental price for studios in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      }
      return `The median rental price for ${beds} bedroom ${propertyTypeDisplay.toLowerCase()}s in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
    }

    return `Optimize your listing with ${city} pricing data and insights`;
  };

  const getMarketInquiriesHeading = () => {
    if (InquiriesDataError) {
      return (
        <Text>
          We are having issues loading your market activity data.{' '}
          <TextButton onClick={() => refetchGetListingPerformance()}>Retry now.</TextButton>
        </Text>
      );
    }

    const {
      marketAverage: { inquiresInFirst10Days, countSinceActivation } = {
        inquiresInFirst10Days: 0,
        countSinceActivation: 0,
      },
    } = inquiriesData || {};

    if (inquiresInFirst10Days && countSinceActivation) {
      const percentOfInquiries = Math.round((inquiresInFirst10Days / countSinceActivation) * 100);
      return `Most listings receive ${percentOfInquiries}% of their inquiries within the first 10 days`;
    }

    return 'Most listings receive a high volume of inquiries within the first 10 days';
  };

  const getInventoryCardHeading = () => {
    if (compsDataError) {
      return (
        <Text>
          We are having issues loading your market comparison data.{' '}
          <TextButton onClick={() => refetchGetComps()}>Retry now.</TextButton>
        </Text>
      );
    }

    let comparativeValue;

    if (!hasEnoughListings) {
      return `In the last 14 days, there have been ${hasListings ? 'very few' : '0'} rentals listed in your market`;
    }

    if (percentChangeValue > 0) {
      comparativeValue = `increased by ${percentChangeValue}%`;
    }
    if (percentChangeValue < 0) {
      comparativeValue = `decreased by ${Math.abs(percentChangeValue)}%`;
    }

    if (percentChangeValue === 0) {
      comparativeValue = 'stayed the same';
    }

    return `In the last ${days} days, the inventory in your market
    has ${comparativeValue}`;
  };

  return (
    <Modal
      data-testid="PremiumPricingGuidanceModal"
      multiModalTriggerId="PremiumPricingGuidanceModal"
      size={{ base: 'fullScreen', lg: 'xl' }}
      header={
        <Heading level={1} textStyle="heading-xs">
          Rent pricing insights
        </Heading>
      }
      trigger={null}
      body={
        <>
          <RentPricingChart isInModal={true} />
          <StyledFlex
            flexDirection={{ default: 'column', lg: 'row' }}
            marginTop="md"
            paddingTop="md"
            paddingBottom="md"
            display="flex"
            justifyContent="space-between"
          >
            <StyledCard cardElevation={0} cardType="secondary">
              <Heading level={6}>{getMedianPriceHeading()}</Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                Review your comparable listings. How do they compare to your property in terms of condition and
                amenities? These resources may help you decide:
              </Paragraph>
              {pricingData?.hasActiveComps && (
                <Button
                  tone="brand"
                  emphasis="outlined"
                  size="sm"
                  onClick={handleCompsButtonClick}
                  css={{ marginBottom: '400' }}
                >
                  View comparable listings
                </Button>
              )}
            </StyledCard>
            <StyledCard cardElevation={0} cardType="secondary">
              <Heading level={6}>{getMarketInquiriesHeading()}</Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                Your first impression on the market is important. Rentals priced about the same as or a bit lower than
                their competition are likely to get the most interest.
              </Paragraph>
              <Paragraph textStyle="body-sm" css={{ marginBottom: '400' }}>
                Consider your goals for price vs. time on market when you&apos;re setting your price.
              </Paragraph>
              {pmrUrl && (
                <TextButton asChild textStyle="body-sm">
                  <a href={pmrUrl.toString()} target="_blank" rel="noreferrer">
                    Explore the pricing tool
                  </a>
                </TextButton>
              )}
            </StyledCard>
            <StyledCard cardElevation={0} cardType="secondary">
              <Heading level={6}>{getInventoryCardHeading()}</Heading>
              <Paragraph textStyle="body-sm" css={{ marginBlock: '400' }}>
                This can impact the average rental price in your market.
              </Paragraph>
            </StyledCard>
          </StyledFlex>
        </>
      }
      footer={
        <Modal.Close>
          <Button size="md" tone="brand" emphasis="filled">
            Done
          </Button>
        </Modal.Close>
      }
    />
  );
};

export default PremiumPricingGuidanceModal;
